<template>
    <div class="toast-container">
        <TransitionGroup name="toast-slide">
            <div v-for="t in toastState.toasts" :key="t.id"
                 class="toast-item" :class="'toast-' + t.type"
                 @click="dismiss(t.id)">
                <Icon :name="iconFor(t.type)" class="toast-icon" />
                <span class="toast-message">{{ t.message }}</span>
                <!-- click.stop because the whole toast dismisses on click, which
                     would swallow the action before it ran. -->
                <button v-if="t.action" class="toast-action" @click.stop="runAction(t)">{{ t.action.label }}</button>
                <button class="toast-close" @click.stop="dismiss(t.id)"><Icon name="x" /></button>
            </div>
        </TransitionGroup>
    </div>
</template>

<script>
export default {
    name: 'ToastContainer',
    inject: {
        toastState: { default: () => ({ toasts: [] }) }
    },
    methods: {
        dismiss(id) {
            this.toastState.toasts = this.toastState.toasts.filter(function(t) { return t.id !== id; });
        },
        runAction(t) {
            // Dismiss first so a slow action cannot be fired twice by an impatient
            // second click, and so the toast does not sit there looking unhandled.
            this.dismiss(t.id);
            try { t.action.run(); } catch (e) { /* the action reports its own failure */ }
        },
        iconFor(type) {
            var icons = {
                success: 'circle-check',
                error: 'exclamation-circle',
                warning: 'triangle-alert',
                info: 'info'
            };
            return icons[type] || icons.info;
        }
    }
};
</script>

<style scoped>
.toast-container {
    position: fixed;
    top: 64px;
    right: 16px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 380px;
}
.toast-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 8px;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    border-left: 4px solid;
    min-width: 280px;
}
.toast-success { border-color: #059669; }
.toast-success .toast-icon { color: #059669; }
.toast-error { border-color: #ef4444; }
.toast-error .toast-icon { color: #ef4444; }
.toast-warning { border-color: #f59e0b; }
.toast-warning .toast-icon { color: #f59e0b; }
.toast-info { border-color: var(--jb-primary); }
.toast-info .toast-icon { color: var(--jb-primary); }
.toast-icon { font-size: 18px; flex-shrink: 0; }
.toast-message { font-size: 14px; color: rgba(0, 0, 0, 0.9); flex: 1; }
/* Reads as the one thing to press in the toast, so it is weighted above the
   message and clearly separated from the dismiss X next to it. */
.toast-action {
    border: none;
    background: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    color: var(--jb-primary-dark);
    padding: 2px 6px;
    flex-shrink: 0;
    border-radius: var(--jb-radius);
    text-decoration: underline;
}
.toast-action:hover { background: rgba(var(--jb-primary-rgb), 0.08); }
.toast-close {
    border: none;
    background: none;
    cursor: pointer;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.4);
    padding: 0;
    flex-shrink: 0;
    line-height: 1;
}
.toast-close:hover { color: rgba(0, 0, 0, 0.8); }

/* TransitionGroup animations */
.toast-slide-enter-active { transition: all 0.3s ease; }
.toast-slide-leave-active { transition: all 0.3s ease; position: absolute; right: 0; }
.toast-slide-enter-from { opacity: 0; transform: translateX(80px); }
.toast-slide-leave-to { opacity: 0; transform: translateX(80px); }
.toast-slide-move { transition: transform 0.3s ease; }

@media (max-width: 480px) {
    .toast-container {
        left: 8px;
        right: 8px;
        max-width: none;
    }
    .toast-item { min-width: 0; }
}
</style>
